
import { VueRecaptcha } from "vue-recaptcha";
import useVuelidate from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { contentService } from "@/helpers/api-services/content.service";
import { GoogleTrackingService } from "@/helpers/tracking-services/google-tracking-service";
import { lcService } from "@/helpers/api-services/lc.service";
import { servicesService } from "@/helpers/api-services/services.service";
import { profileComputed } from "@/state/helpers";
import { authComputed } from "@/state/helpers";
import i18n from "@/i18n";
import LeadCapture from "@/components/lead-capture.vue";
import { reactive } from "vue";
import {
  KcmCategory,
  KcmProfileField,
  LeadCapturePostData,
  LeadCategories,
} from "@/types";

import { defineComponent } from "vue";

declare interface ComponentData {
  categories: KcmCategory[] | undefined;
  newCategory: string;
  profileImage: string;
  logoImage: string | null;
  textButton1: string;
  textButton2: string;
  searchText: string;
  mountedFinished: boolean;
  messageSent: boolean;
  modalPhone: string;
  modalSubject: string;
  modalMessage: string;
  sitekey: string;
  reCaptcha: boolean;
  reCapError: boolean;
}

export default defineComponent({
  components: { LeadCapture, VueRecaptcha },

  setup() {
    const state = reactive({
      modalName: "",
      modalEmail: "",
    });
    const validations = {
      modalName: { required },
      modalEmail: { required, email },
    };
    return { state, vs$: useVuelidate(validations, state) };
  },

  data(): ComponentData {
    return {
      categories: undefined,
      newCategory: "",
      profileImage: "",
      logoImage: null,
      textButton1: this.$t("sidebar.attractButton1") as string,
      textButton2: this.$t("sidebar.attractButton2") as string,
      searchText: "",
      mountedFinished: false,
      messageSent: false,
      modalPhone: "",
      modalSubject: "",
      modalMessage: "",
      sitekey: process.env.VUE_APP_RECAPTCHA_SITE_KEY ?? "",
      reCaptcha: false,
      reCapError: false,
    };
  },

  props: {
    lead: {
      type: Object as () => LeadCategories,
      required: true,
    },

    searchType: {
      default: "blog",
      type: String,
    },
  },

  async mounted(): Promise<void> {
    contentService.getCategories().then((result) => {
      this.categories = result.categories;
    });

    if (this.profilePP && this.profilePP.basic) {
      if (this.profilePP.basic.photo && this.profilePP.basic.photo.value) {
        this.profileImage = this.profilePP.basic.photo.value;
      }
      if (this.profilePP.basic.logo && this.profilePP.basic.logo.value) {
        this.logoImage = this.profilePP.basic.logo.value;
      }
    } else if (!this.profilePP) {
      this.profileImage =
        "https://files.simplifyingthemarket.com/assets/STMSidebar.jpg";
    }
    this.mountedFinished = true;
  },

  methods: {
    localeToString: contentService.localeToString,
    getStyle(textObject: KcmProfileField) {
      let string = "font-size: " + textObject.fontSize + "px";
      return string;
    },

    checkGuide(guide: string): boolean | undefined {
      if (
        this.profilePP &&
        this.profilePP.blog &&
        this.profilePP.blog.enabled_pages &&
        this.profilePP.blog.enabled_pages.value
      ) {
        try {
          return JSON.parse(this.profilePP.blog.enabled_pages.value)[guide];
        } catch {
          return false;
        }
      }
      return { buyer: true, seller: true, fthb: false }[guide];
    },

    newSearch(e: Event): void {
      e.preventDefault();
      let route =
        "/" +
        i18n.locale +
        "/search/?s=" +
        this.searchText +
        "&t=" +
        this.searchType;
      if (this.ppCode) {
        route = route + `&a=` + this.ppCode;
      }
      this.$router.push(route);
    },

    newCategoryPage(): void {
      let route =
        "/" +
        i18n.locale +
        "/category/" +
        this.newCategory +
        "/?t=" +
        this.searchType;
      if (this.ppCode) {
        route = route + `&a=` + this.ppCode;
      }
      this.$router.push(route);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    verifyMethod(response: any): void {
      this.reCaptcha = true;
      this.reCapError = false;
      servicesService
        .postRecaptchaResponse({ site: "stm", client_response: response })
        .then((result) => {
          console.log(result);
        })
        .catch((error) => alert(error.message));
    },

    expiredMethod(): void {
      this.reCaptcha = false;
    },

    async sendMessage(): Promise<void> {
      this.vs$.modalName.$touch();
      this.vs$.modalEmail.$touch();
      if (
        !this.vs$.modalName.$invalid &&
        !this.vs$.modalEmail.$invalid &&
        this.reCaptcha
      ) {
        await lcService
          .postLead(this.lcData)
          .then((result) => {
            if (result.error) {
              alert(result.error);
            } else {
              this.messageSent = true;
              setTimeout(() => {
                this.$bvModal.hide("modal-1");
              }, 2000);

              /** offset hiding the success message so it doesn't flash the form */
              setTimeout(() => {
                this.messageSent = false;
              }, 4000);
            }
          })
          .catch((error) => alert(error.message));
      } else if (!this.vs$.$invalid && !this.reCaptcha) {
        this.reCapError = true;
      }
    },

    // Need to actually type this one day...
    setUpRecaptcha(): void {
      if (document.querySelector('[title="reCAPTCHA"]')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const reCaptcha = document.querySelector('[title="reCAPTCHA"]') as any;
        reCaptcha.title = reCaptcha.title + "modal-1";
        reCaptcha.src = reCaptcha.src.replace(/(&hl=e.)/, "&hl=" + i18n.locale);
      }
    },

    categoryText: contentService.categoryText,
    gtagFocusBlur: GoogleTrackingService.gtagFocusBlur,
  },

  computed: {
    ...profileComputed,
    ...authComputed,

    checkLC(): boolean {
      return (
        this.profilePP &&
        this.profilePP.blog &&
        this.profilePP.blog.lead_capture &&
        this.profilePP.blog.lead_capture.value &&
        this.profilePP.blog.lead_capture.value === "on"
      );
    },

    checkLogo(): boolean {
      return (
        this.profilePP &&
        this.profilePP.blog &&
        this.profilePP.blog.logo_pos &&
        this.profilePP.blog.logo_pos.value &&
        this.profilePP.blog.logo_pos.value === "2"
      );
    },

    websiteLink(): string {
      return "http://" + this.profilePP.basic.website.value;
    },

    facebookLink(): string {
      return "http://www.facebook.com/" + this.profilePP.basic.facebook.value;
    },

    twitterLink(): string {
      return "http://www.twitter.com/" + this.profilePP.basic.twitter.value;
    },

    linkedinLink(): string {
      return "http://www.linkedin.com/" + this.profilePP.basic.linkedin.value;
    },

    youtubeLink(): string {
      return "http://www.youtube.com/" + this.profilePP.basic.youtube.value;
    },

    pinterestLink(): string {
      return "http://www.pinterest.com/" + this.profilePP.basic.pinterest.value;
    },

    instagramLink(): string {
      return "http://www.instagram.com/" + this.profilePP.basic.instagram.value;
    },

    lcData(): LeadCapturePostData {
      return {
        auth_id: this.authUserID,
        member_email:
          this.profilePP &&
          this.profilePP.blog &&
          this.profilePP.blog.lead_email &&
          this.profilePP.blog.lead_email.value
            ? this.profilePP.blog.lead_email.value
            : "",
        name: this.state.modalName,
        lead_email: this.state.modalEmail,
        phone: this.modalPhone,
        subject: this.modalSubject
          ? this.modalSubject
          : "<DEFAULT SUBJECT HERE>",
        message: this.modalMessage,
        page_viewed: window.location.href,
      };
    },
  },

  watch: {
    profilePP: {
      handler(): void {
        if (
          this.profilePP &&
          this.profilePP.basic &&
          this.profilePP.basic.photo &&
          this.profilePP.basic.photo.value
        ) {
          this.profileImage = this.profilePP.basic.photo.value;
        }
        if (
          this.profilePP &&
          this.profilePP.basic &&
          this.profilePP.basic.logo &&
          this.profilePP.basic.logo.value
        ) {
          this.logoImage = this.profilePP.basic.logo.value;
        }
      },
      deep: true,
    },
  },
});
